import GeaoLayout from "../shared/Layout/GeaoLayout";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useState, useContext, useRef, useEffect } from "react";
import { classes } from "../../../../util/style-utils";
import { useNavigate } from "react-router-dom";
import { GeaoPaths } from "../../constants/GeaoPaths";
import { useLocation } from "react-router-dom";
import { VerificationMethods } from "./RecallHelpers";
import { sendOtpText, sendOtpEmail, authenticateOtp } from "../../../../api/otp";
import Toast, { ToastTypes } from "../../../gdk/Toast/Toast";
import { retrieveIdentityForInterview } from "../../../../api/agent-profile";
import { GeaoContext } from "../../GeaoContextProvider";

const validationSchema = yup.object().shape({
    verificationCode: yup.string().required("Please enter your verification code.")
});

export default function RecallVerification() {
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema)
    });

    const location = useLocation();
    const [verificationMethod] = useState(location.state?.method);
    const [verificationValue] = useState(location.state?.verificationValue);
    const [otpData, setOtpData] = useState(location.state?.otpData);
    const [showAlert, setShowAlert] = useState(false);
    const resendSuccessfulToastRef = useRef();
    const resendFailedToastRef = useRef();
    const [buttonsDisabled, setButtonsDisabled] = useState(false);
    const { setAgentInterview, setAgencyInterview } = useContext(GeaoContext);

    function onSubmit(values) {
        setButtonsDisabled(true);

        authenticateOtp({ ...otpData, otp: values?.verificationCode },
            (response) => {
                handleRedirectBasedOnRecallResponse(response);
                setAgentInterview(response?.interview);
                setAgencyInterview(response?.agencyInterview);
            },
            (response) => {
                if (typeof response === typeof {} && response?.some(item => item?.noticeId === "MSG150-9630434")) {
                    navigate(GeaoPaths.CreateYourProfileChecklist, { state: { alert: "maximumTries" } });
                }
                else if (typeof response === typeof {} && (response?.some(item => item?.noticeId === "MSG150-9630430") || response?.some(item => item?.noticeId === "MSG150-8"))) {
                    setShowAlert(true);
                }
                else {
                    navigate(GeaoPaths.TechnicalDifficulties);
                }
            },
            () => {
                setButtonsDisabled(false);
            }
        )
    }

    function onResendCode() {
        setButtonsDisabled(true);

        if (verificationMethod === VerificationMethods.Text) {
            sendOtpText(
                (response) => {
                    setOtpData(response);
                    resendSuccessfulToastRef.current.show();
                    setShowAlert(false);
                },
                () => {
                    resendFailedToastRef.current.show();
                },
                () => {
                    setButtonsDisabled(false);
                },

            );
        } else if (verificationMethod === VerificationMethods.Email) {
            sendOtpEmail(
                (response) => {
                    setOtpData(response);
                    resendSuccessfulToastRef.current.show();
                    setShowAlert(false);
                },
                () => {
                    resendFailedToastRef.current.show();
                },
                () => {
                    setButtonsDisabled(false);
                },
            );
        } else {
            setButtonsDisabled(false);
            return;
        }
    }

    async function handleRedirectBasedOnRecallResponse(recallResponse) {
        let outcome = recallResponse?.interview?.outcome?.toLowerCase();
        let role = recallResponse?.interview?.questionAnswers?.Role?.answer?.toLowerCase();

        // Incomplete Interview Outcomes
        if ([Roles.Principal, Roles.AuthorizedAppointmentRep].includes(role) && outcome === Outcomes.Incomplete) {
            return navigate(GeaoPaths.PrincipalApplicationInProgress);
        }

        if ([Roles.Producer, Roles.Csr, Roles.BranchManager, Roles.OtherEmployee].includes(role) && outcome === Outcomes.Incomplete) {
            return navigate(GeaoPaths.ProducerApplicationInProgress);
        }

        // Ineligible Interview Outcomes
        if (Object.values(Roles).includes(role) && outcome === Outcomes.Ineligible) {
            return navigate(GeaoPaths.CreateYourProfileKickout);
        }

        // Processing Interview Outcomes
        if ([Roles.Principal, Roles.AuthorizedAppointmentRep].includes(role) && outcome === Outcomes.Processing) {
            return navigate(GeaoPaths.PrincipalBackgroundCheckInProgress);
        }

        if ([Roles.Producer, Roles.Csr, Roles.BranchManager, Roles.OtherEmployee].includes(role) && outcome === Outcomes.Processing) {
            return navigate(GeaoPaths.ProducerBackgroundCheckInProgress);
        }

        // Complete Interview Outcomes
        if (Object.values(Roles).includes(role) && outcome === Outcomes.Complete && recallResponse?.data?.SOB?.startsWith("I")) {
            return await handleCompleteInterviewOutcome(role);
        }

        // Fallback for unexpected outcomes
        return navigate(GeaoPaths.TechnicalDifficulties);
    }

    async function handleCompleteInterviewOutcome(role) {
        const profile = await new Promise((resolve, reject) => {
            retrieveIdentityForInterview(
                (result) => resolve(result),
                (error) => reject(error)
            );
        });

        const profileIsComplete = profile?.profileIsComplete;
        const backgroundInvestigationResult = profile?.agentProfile?.backgroundInvestigationResult;

        if (backgroundInvestigationResult?.code === BackgroundInvestigationCodes.FAIL) {
            return navigate(GeaoPaths.BackgroundCheckDenial);
        }

        const isPrincipalRole = [Roles.Principal, Roles.AuthorizedAppointmentRep].includes(role);
        const isProducerRole = [Roles.Producer, Roles.Csr, Roles.BranchManager, Roles.OtherEmployee].includes(role);

        if (!profileIsComplete) {
            if (isPrincipalRole) {
                return navigate(GeaoPaths.PrincipalBackgroundCheckInProgress);
            } else if (isProducerRole) {
                return navigate(GeaoPaths.ProducerBackgroundCheckInProgress);
            }
        } else if (isPrincipalRole) {
            return navigate(GeaoPaths.PrincipalApplicationComplete);
        } else if (isProducerRole) {
            return navigate(GeaoPaths.ProducerApplicationComplete);
        }
    }

    return (
        <GeaoLayout header="Verification">
            <Toast trackingId="Recall:RecallVerification:CodeResent"
                toastRef={resendSuccessfulToastRef}
                header="Success"
                description="Your OTP was resent successfully."
                toastType={ToastTypes.Confirmation}
            />

            <Toast trackingId="Recall:RecallVerification:ResendCodeError"
                toastRef={resendFailedToastRef}
                header="Error"
                description="Your OTP failed to be resent. Please try again."
                toastType={ToastTypes.Info}
            />

            <form className="form geao-form" onSubmit={handleSubmit(onSubmit)} style={{ width: "750px" }} >
                <h2 className="margin-bottom">
                    Check your {verificationMethod === VerificationMethods.Text ? "phone" : "email"} for a code.
                </h2>
                <p className="text">
                    We sent a code to {verificationMethod === VerificationMethods.Text ? verificationValue : "your email"}.
                    Please enter it below to complete verification.<br />
                    The code will expire 10 minutes after being sent.
                </p>
                <div className="flex-row">
                    <div className={classes("form-field", errors.verificationCode && "form-field--error")}>
                        <input
                            type="text"
                            placeholder="Verification Code"
                            {...register("verificationCode")}
                        />
                        {errors.verificationCode && (
                            <span className="form-message">
                                {errors.verificationCode.message}
                            </span>
                        )}
                    </div>
                </div>
                {showAlert &&
                    <div className="flex-row">
                        <div className="alert">
                            <div className="alert--high-importance">
                                <span className="icon-alert" />
                                <ul className="alert-list">
                                    <li className="alert-content">
                                        <div className="h4">Invalid or Expired Code</div>
                                        <p>
                                            This code is invalid or expired. Please try again, resend code, or confirm that you have an active application.
                                        </p>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                }
                <div className="flex-row">
                    <div className="col-sm-12">
                        <button data-onboarding-button-id="Recall:RecallVerification:Verify"
                            type="submit"
                            className="btn btn--primary btn--full-mobile btn--pull-right"
                            disabled={buttonsDisabled}
                        >
                            <span>Submit Code</span>
                        </button>
                        <button data-onboarding-button-id="Recall:RecallVerification:ResendCode"
                            type="button"
                            className="btn btn--secondary btn--full-mobile btn--pull-right"
                            onClick={onResendCode}
                            disabled={buttonsDisabled}
                        >
                            <span>Resend Code</span>
                        </button>
                        <button data-onboarding-button-id="Recall:RecallVerification:Cancel"
                            type="submit"
                            className="btn btn--destructive btn--full-mobile btn--pull-left"
                            onClick={() => navigate(GeaoPaths.ApplicationStatus)}
                            disabled={buttonsDisabled}
                        >
                            <span>Back</span>
                        </button>
                    </div>
                </div>

                {verificationMethod !== VerificationMethods.Text &&
                    <div className="highlight-messaging">
                        <div>
                            <span className="geico-icon icon-info"></span>
                        </div>
                        <div>
                            <div className="highlight-messaging--heading">Didn't receive a code?</div>
                            <div className="highlight-messaging--message">Please go back and re-enter your email address, or check your spam folder.</div>
                        </div>
                    </div>
                }
            </form>
        </GeaoLayout>
    );
}

const Roles = {
    Principal: "principal",
    AuthorizedAppointmentRep: "authorized-appointment-rep",
    Producer: "producer",
    Csr: "csr",
    BranchManager: "branch-manager",
    OtherEmployee: "other-employee"
};

const Outcomes = {
    Incomplete: "incomplete",
    Ineligible: "ineligible",
    Processing: "processing",
    Complete: "complete"
};

const BackgroundInvestigationCodes = {
    RVW: "RVW",
    PASS: "PASS",
    FAIL: "FAIL"
};
